import SovHeatmap from "./SovHeatmap";
import { DoneRounded as MatchIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Grid,
  Stack,
  TextField as MuiTextField,
  Typography,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import * as numbers from "common/numbers.js";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import SmallLoader from "components/common/SmallLoader";
import TextField from "components/common/TextField";
import Visible from "components/common/Visible";
import * as pricingHooks from "components/customPricing/dnfModel1/pricingHooks";
import { useDispatch, useSelector } from "react-redux";
import * as sovService from "services/sovService";
import * as staticDataService from "services/staticDataService";
import * as SOVStore from "store/SOV";
import * as analyticsStore from "store/analytics";
import { useSovAnalytics } from "store/hooks/sovHooks";

const useProgramPricing = ({ pricingMethod } = {}) => {
  pricingMethod = pricingMethod ?? "programPricing";

  const { isFetching } = pricingHooks.usePricing({ pricingMethod });

  const programPricing = useSelector(analyticsStore.select)?.[pricingMethod];

  return {
    programPricing,
    isProgramPricingFetching: isFetching,
  };
};

const AddressInputs = ({ location, updateLocation }) => {
  return (
    <Stack direction={"column"} spacing={2} sx={{ minWidth: 300 }}>
      <TextField
        label={"Street Address"}
        value={location._STREET_ADDRESS || ""}
        onChange={(e) => updateLocation("_STREET_ADDRESS", e.target.value)}
      />
      <TextField
        label={"City"}
        value={location._CITY || ""}
        onChange={(e) => updateLocation("_CITY", e.target.value)}
      />
      <TextField
        label={"State Code"}
        value={location._STATE_CODE || ""}
        onChange={(e) => updateLocation("_STATE_CODE", e.target.value)}
      />
      <TextField
        label={"Zip Code"}
        value={location._ZIP_CODE || ""}
        onChange={(e) => updateLocation("_ZIP_CODE", e.target.value)}
      />
    </Stack>
  );
};

const TivInputs = ({ location, updateLocation }) => {
  const tivComponentsTotal = (location._PD ?? 0) + (location._ALE ?? 0);
  const showTivMatch =
    location._TIV != null &&
    numbers.approxEqual(location._TIV, tivComponentsTotal, 0.1);
  const pdComponentsTotal =
    (location._PD_BUILDING ?? 0) +
    (location._PD_APPURTENANT_STRUCTURES ?? 0) +
    (location._PD_CONTENTS ?? 0);
  const showPdMatch =
    location._PD != null &&
    numbers.approxEqual(location._PD, pdComponentsTotal, 0.1);

  return (
    <Stack direction={"column"} spacing={1} sx={{ minWidth: 300 }}>
      <Stack direction={"row"} alignItems={"center"}>
        <Typography variant={"subtitle1"}>{"TIV"}</Typography>
        {showTivMatch && (
          <Tooltip title={"Total matches PD + ALE"} arrow>
            <MatchIcon
              fontSize={"1rem"}
              color={"success"}
              sx={{ marginLeft: "3px" }}
            />
          </Tooltip>
        )}
      </Stack>
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <PrettyNumberTextField
          label={"TIV"}
          value={location._TIV ?? null}
          onChangeNumber={(x) => updateLocation("_TIV", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant={"body1"} sx={{ paddingLeft: 2, paddingRight: 2 }}>
          {"="}
        </Typography>
        <PrettyNumberTextField
          label={"PD"}
          value={location._PD ?? null}
          onChangeNumber={(x) => updateLocation("_PD", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant={"body1"} sx={{ paddingLeft: 2, paddingRight: 2 }}>
          {"+"}
        </Typography>
        <PrettyNumberTextField
          label={"ALE"}
          value={location._ALE ?? null}
          onChangeNumber={(x) => updateLocation("_ALE", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        sx={{ paddingTop: "15px" }}
      >
        <Typography variant={"subtitle1"}>{"PD Breakdown"}</Typography>
        {showPdMatch && (
          <Tooltip title={"Total matches overall PD"} arrow>
            <MatchIcon
              fontSize={"1rem"}
              color={"success"}
              sx={{ marginLeft: "3px" }}
            />
          </Tooltip>
        )}
      </Stack>
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <PrettyNumberTextField
          label={"Building"}
          value={location._PD_BUILDING ?? null}
          onChangeNumber={(x) => updateLocation("_PD_BUILDING", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant={"body1"} sx={{ paddingLeft: 2, paddingRight: 2 }}>
          {"+"}
        </Typography>
        <PrettyNumberTextField
          label={"Appurtenant Structures"}
          value={location._PD_APPURTENANT_STRUCTURES ?? null}
          onChangeNumber={(x) =>
            updateLocation("_PD_APPURTENANT_STRUCTURES", x)
          }
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant={"body1"} sx={{ paddingLeft: 2, paddingRight: 2 }}>
          {"+"}
        </Typography>
        <PrettyNumberTextField
          label={"Contents"}
          value={location._PD_CONTENTS ?? null}
          onChangeNumber={(x) => updateLocation("_PD_CONTENTS", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <Typography variant={"caption"}>
        {`Total: ${numbers.currencyFormat(pdComponentsTotal)}`}
      </Typography>
    </Stack>
  );
};

const CommentInputs = ({ location, updateLocation }) => {
  return (
    <Stack direction={"column"} spacing={1} sx={{ minWidth: 300, flex: 1 }}>
      <Typography variant={"subtitle1"}>{"Comments"}</Typography>
      <TextField
        value={location._COMMENT || ""}
        onChange={(e) => updateLocation("_COMMENT", e.target.value)}
        variant={"filled"}
        size={"small"}
        hiddenLabel
        multiline
        rows={6}
      />
    </Stack>
  );
};

const RatesInputs = ({
  location,
  updateLocation,
  resolvedLocation,
  programPricing,
  isProgramPricingFetching,
}) => {
  const pricing = programPricing?.values?.sov?.[resolvedLocation?.id] ?? {};

  return (
    <Stack direction={"column"} spacing={2} sx={{ minWidth: 300 }}>
      <Stack direction={"row"} spacing={2}>
        <PrettyNumberTextField
          label={"Auto (%)"}
          value={pricing._AUTO_RATE_PCT ?? null}
          variant={"filled"}
          size={"small"}
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                {isProgramPricingFetching ? (
                  <SmallLoader />
                ) : (
                  <div style={{ width: "40px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
        <PrettyNumberTextField
          label={"Manual (%)"}
          value={location._MANUAL_RATE_PCT ?? null}
          onChangeNumber={(x) => updateLocation("_MANUAL_RATE_PCT", x)}
          variant={"filled"}
          size={"small"}
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
        />
        <PrettyNumberTextField
          label={"Risk Adj (%)"}
          value={pricing._RISK_ADJ_PCT ?? null}
          variant={"filled"}
          size={"small"}
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                {isProgramPricingFetching ? (
                  <SmallLoader />
                ) : (
                  <div style={{ width: "40px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack direction={"row"} spacing={2}>
        <PrettyNumberTextField
          label={"Location Value"}
          value={pricing._LOCATION_VALUE ?? null}
          variant={"filled"}
          size={"small"}
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                {isProgramPricingFetching ? (
                  <SmallLoader />
                ) : (
                  <div style={{ width: "40px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
        <PrettyNumberTextField
          label={"Total Value"}
          value={pricing._TOTAL_VALUE ?? null}
          variant={"filled"}
          size={"small"}
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                {isProgramPricingFetching ? (
                  <SmallLoader />
                ) : (
                  <div style={{ width: "40px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
        <PrettyNumberTextField
          label={"FGU Premium"}
          value={pricing._FGU_PREMIUM ?? null}
          variant={"filled"}
          size={"small"}
          disabled
          inputProps={{ style: { textAlign: "right" } }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position={"start"}>
                {isProgramPricingFetching ? (
                  <SmallLoader />
                ) : (
                  <div style={{ width: "40px" }} />
                )}
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </Stack>
  );
};

const LoadsInputs = ({ location, updateLocation, sovConfig }) => {
  const getEnumChoices = (enumKey) => {
    return sovConfig?.enums?.[enumKey]?.choices ?? [];
  };

  const loads = [
    {
      label: "Occupancy Segment",
      field: "_OCCUPANCY_SEGMENT",
      enumKey: "occupancySegment",
    },
    {
      label: "Risk Management",
      field: "_RISK_MANAGEMENT",
      enumKey: "riskManagement",
    },
    {
      label: "Fire Protection",
      field: "_FIRE_PROTECTION",
      enumKey: "fireProtection",
    },
    { label: "Construction", field: "_CONSTRUCTION", enumKey: "construction" },
    {
      label: "Occupancy Hazard",
      field: "_OCCUPANCY_HAZARD",
      enumKey: "occupancyHazard",
    },
    {
      label: "RZ Risk Score",
      field: "_RZ_RISK_ZONE",
      enumKey: "rzRiskZone",
    },
    {
      label: "ISO Protection Class",
      field: "_ISO_FIRE_PROTECTION_CLASS",
      enumKey: "isoFireProtectionClass",
    },
  ];

  return (
    <Stack direction={"column"} spacing={2} sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ margin: 0, width: "100%" }}>
        {loads.map(({ label, field, enumKey }) => {
          const choices = getEnumChoices(enumKey);
          const selectedChoice =
            choices.find((c) => c.key === location[field]) || null;

          return (
            <Grid item xs={12} sm={6} md={4} key={field}>
              <Autocomplete
                options={choices}
                value={selectedChoice}
                onChange={(_, newValue) =>
                  updateLocation(field, newValue?.key ?? null)
                }
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.key === value?.key
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label={label}
                    variant={"filled"}
                    size={"small"}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};

const PropertySov = () => {
  const dispatch = useDispatch();
  const sovInput = useSelector(SOVStore.selectSOVInput);

  const location = sovInput?.manualLocations?.[0] || {};

  const { data: sovConfig } = staticDataService.useConfigQuery("sov");

  const updateLocation = (field, value) => {
    const updatedLocation = {
      ...location,
      [field]: value,
    };
    dispatch(
      SOVStore.updateSOV({
        manualLocations: [updatedLocation],
      })
    );
  };

  const {
    data: resolvedSov,
    isLoading: isResolvedSovFetching,
  } = sovService.useResolveSovQuery({ sovInput, geolocate: true });

  const resolvedLocation = resolvedSov?.locations?.[0];

  const { programPricing, isProgramPricingFetching } = useProgramPricing({
    pricingMethod: sovConfig?.pricingMethod ?? null,
  });

  useSovAnalytics();

  return (
    <>
      <Component
        title={"Property Details"}
        options={
          <Disabled ifReadOnly>
            <Button
              size={"small"}
              onClick={() => dispatch(SOVStore.setSOV({}))}
            >
              {"Clear"}
            </Button>
          </Disabled>
        }
      />

      <Component>
        <Stack direction={"row"} spacing={2}>
          <AddressInputs location={location} updateLocation={updateLocation} />
          <Visible byTag={"<SOV>.heatmap"} defaultVisible={true}>
            <SovHeatmap
              sov={resolvedSov}
              loading={isResolvedSovFetching}
              height={"unset"}
            />
          </Visible>
        </Stack>
      </Component>

      <Component title={"Property Value"}>
        <Stack direction={"row"} spacing={2}>
          <TivInputs location={location} updateLocation={updateLocation} />
          <CommentInputs location={location} updateLocation={updateLocation} />
        </Stack>
      </Component>

      <Component title={"Loads"}>
        <LoadsInputs
          location={location}
          updateLocation={updateLocation}
          sovConfig={sovConfig}
        />
      </Component>

      <Component title={"Rates"}>
        <RatesInputs
          location={location}
          updateLocation={updateLocation}
          resolvedLocation={resolvedLocation}
          programPricing={programPricing}
          isProgramPricingFetching={isProgramPricingFetching}
        />
      </Component>
    </>
  );
};

export default PropertySov;
